import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import { createQueryParams } from "features/config/helpers";
import { selectGetAdminSession } from "features/admin/presentation/slices/get-admin-session.slice";
import { AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { NoticeModel } from "features/stock-ordering/core/domain/notice.model";
import {
  getNotices,
  selectGetNotices,
  GetNoticesState,
} from "../slices/get-notices.slice";
import {
  getNoticeComments,
  selectGetNoticeComments,
  resetGetNoticeComments,
} from "../slices/get-notice-comments.slice";
import {
  postNoticeComment,
  selectPostNoticeComment,
  resetPostNoticeComment,
  PostNoticeCommentState,
} from "../slices/post-notice-comment.slice";
import { CommentSection } from "features/shared/presentation/components";
import { NoticeBanner, NoticeBannerContent } from "./stock-ordering-utils";
import moment from "moment";

type SelectedNotice = NoticeModel | "default";

export function DynamicNoticeBanner() {
  const dispatch = useAppDispatch();
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const getNoticesState = useAppSelector(selectGetNotices);
  const getNoticeCommentsState = useAppSelector(selectGetNoticeComments);
  const postNoticeCommentState = useAppSelector(selectPostNoticeComment);

  const [selectedNotice, setSelectedNotice] = useState<SelectedNotice | null>(
    null
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const isDefaultSelected = selectedNotice === "default";

  useEffect(() => {
    const query = createQueryParams({ limit: 10, page: 1 });
    dispatch(getNotices(query));
  }, [dispatch]);

  // Auto-select the latest notice, or fall back to default if none exist
  useEffect(() => {
    if (
      getNoticesState.status === GetNoticesState.success &&
      getNoticesState.data &&
      !selectedNotice
    ) {
      const notices = getNoticesState.data.notices;
      if (notices.length > 0) {
        setSelectedNotice(notices[0]);
      } else {
        setSelectedNotice("default");
      }
    }
  }, [getNoticesState, selectedNotice]);

  // Fetch comments when a DB notice is selected
  useEffect(() => {
    if (selectedNotice && selectedNotice !== "default") {
      const query = createQueryParams({
        noticeId: selectedNotice.id,
        commentPage: 1,
        commentPerPage: 25,
      });
      dispatch(getNoticeComments(query));
    }
  }, [dispatch, selectedNotice]);

  // Refresh comments after posting
  useEffect(() => {
    if (postNoticeCommentState.status === PostNoticeCommentState.success) {
      dispatch(resetPostNoticeComment());
      if (selectedNotice && selectedNotice !== "default") {
        const query = createQueryParams({
          noticeId: selectedNotice.id,
          commentPage: 1,
          commentPerPage: 25,
        });
        dispatch(getNoticeComments(query));
      }
    }
  }, [postNoticeCommentState, dispatch, selectedNotice]);

  const handleSubmitComment = (comment: string, reply: number | null) => {
    const { user_id } = getAdminSessionState.data?.admin ?? { user_id: null };

    if (selectedNotice && selectedNotice !== "default") {
      dispatch(
        postNoticeComment({
          comment: comment,
          userId: user_id,
          noticeId: selectedNotice.id,
          replyToId: reply ?? null,
        })
      );
    }
  };

  const handleSelectNotice = (notice: SelectedNotice) => {
    setSelectedNotice(notice);
    setShowDropdown(false);
    setShowComments(false);
    if (notice !== "default") {
      dispatch(resetGetNoticeComments());
    }
  };

  const notices = getNoticesState.data?.notices ?? [];
  const commentData = getNoticeCommentsState.data?.commentData ?? [];
  const pagination = getNoticeCommentsState.data?.pagination;

  // Show the full static NoticeBanner while loading or if no selection yet
  if (getNoticesState.status !== GetNoticesState.success || !selectedNotice) {
    return <NoticeBanner />;
  }

  // Total dropdown items = DB notices + the default notice
  const hasMultipleOptions = notices.length > 0;

  // Get the current title for the header
  const currentTitle = isDefaultSelected
    ? "Important Notice: Updated Delivery Lead Times"
    : selectedNotice.title;

  return (
    <div className="w-full font-[Roboto]">
      <div
        role="alert"
        className="relative flex flex-col p-5 bg-yellow-50 w-full border border-yellow-300 shadow-md rounded-lg"
      >
        <div className="flex">
          <span className="self-start text-yellow-700 mr-3">
            <AlertCircle size={26} />
          </span>

          <div className="flex flex-col flex-1">
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-2 text-left group"
              >
                <span className="block font-bold text-lg text-yellow-900 group-hover:text-yellow-700 transition-colors">
                  {currentTitle}
                </span>
                {hasMultipleOptions &&
                  (showDropdown ? (
                    <ChevronUp size={20} className="text-yellow-700" />
                  ) : (
                    <ChevronDown size={20} className="text-yellow-700" />
                  ))}
              </button>

              {showDropdown && hasMultipleOptions && (
                <div className="absolute top-full left-0 mt-1 w-full max-w-lg bg-white border border-yellow-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                  {/* Default static notice entry */}
                  <button
                    onClick={() => handleSelectNotice("default")}
                    className={`w-full text-left px-4 py-3 hover:bg-yellow-50 transition-colors border-b border-yellow-100 ${
                      isDefaultSelected ? "bg-yellow-100 font-semibold" : ""
                    }`}
                  >
                    <span className="block text-sm text-yellow-900 font-medium">
                      Important Notice: Updated Delivery Lead Times
                    </span>
                    <span className="block text-xs text-yellow-600 mt-0.5">
                      September 10, 2025
                    </span>
                  </button>

                  {/* Database notices */}
                  {notices.map((notice) => (
                    <button
                      key={notice.id}
                      onClick={() => handleSelectNotice(notice)}
                      className={`w-full text-left px-4 py-3 hover:bg-yellow-50 transition-colors border-b border-yellow-100 last:border-b-0 ${
                        !isDefaultSelected &&
                        selectedNotice.id === notice.id
                          ? "bg-yellow-100 font-semibold"
                          : ""
                      }`}
                    >
                      <span className="block text-sm text-yellow-900 font-medium">
                        {notice.title}
                      </span>
                      <span className="block text-xs text-yellow-600 mt-0.5">
                        {notice.published_at
                          ? moment(notice.published_at).format("MMMM D, YYYY")
                          : "Draft"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Content area: default static content or dynamic DB notice */}
            {isDefaultSelected ? (
              <NoticeBannerContent />
            ) : (
              <>
                <span className="block text-yellow-700 text-xs mt-1 italic">
                  Notice released on:{" "}
                  {selectedNotice.published_at
                    ? moment(selectedNotice.published_at).format(
                        "MMMM D, YYYY"
                      )
                    : "Not published"}
                  {selectedNotice.first_name &&
                    ` | Posted by: ${selectedNotice.first_name} ${selectedNotice.last_name}`}
                </span>

                <div className="text-yellow-800 text-sm mt-3 leading-relaxed whitespace-pre-wrap">
                  {selectedNotice.body}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Comments section — only for DB notices */}
        {!isDefaultSelected && (
          <div className="mt-4 border-t border-yellow-200 pt-3">
            <button
              onClick={() => setShowComments(!showComments)}
              className="text-sm text-yellow-700 hover:text-yellow-900 font-medium transition-colors"
            >
              {showComments ? "Hide Comments" : "Show Comments"}{" "}
              {pagination?.totalComments
                ? `(${pagination.totalComments})`
                : ""}
            </button>

            {showComments && (
              <div className="mt-3">
                <CommentSection
                  commentData={commentData}
                  onCommentSubmit={handleSubmitComment}
                  currentPage={pagination?.commentPage ? 1 : 1}
                  commentMax={
                    pagination?.totalComments
                      ? Number(pagination.totalComments)
                      : 0
                  }
                  commentPerPage={pagination?.commentPerPage ?? 25}
                  onRowsPerPageChange={(event) => {
                    if (selectedNotice) {
                      dispatch(resetGetNoticeComments());
                      const query = createQueryParams({
                        noticeId: selectedNotice.id,
                        commentPage: 1,
                        commentPerPage: event.target.value,
                      });
                      dispatch(getNoticeComments(query));
                    }
                  }}
                  onPageChange={(event, newPage) => {
                    if (selectedNotice) {
                      dispatch(resetGetNoticeComments());
                      const query = createQueryParams({
                        noticeId: selectedNotice.id,
                        commentPage: newPage,
                        commentPerPage: pagination?.commentPerPage ?? 25,
                      });
                      dispatch(getNoticeComments(query));
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
